import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConsoleServicesProvider, type ConsoleServices } from './console-services.jsx';
import { flowRoutes, routeForPath } from './flow-routes.jsx';
import { pathWithoutHandoff, readLandingHandoff } from './landing-handoff.js';
import type { OnboardingStore } from './onboarding-storage.js';
import { paths } from './paths.js';
import { normalizePath, pushPath, replacePath, useVisiblePath } from './router.js';
import { SessionStatus } from './session-status.jsx';
import { StepRail } from './step-rail.jsx';
import { StepShell } from './step-shell.jsx';
import { NavigationButtons } from './navigation-buttons.jsx';
import { OnboardingProvider, useOnboarding } from './use-onboarding.jsx';
import { useSession, type SessionStatus as SessionAuthStatus } from './use-session.js';

export interface AppProps {
  /** Built at the composition root; takes getToken because only Clerk can mint one. */
  createServices: (getToken: () => Promise<string | null>) => ConsoleServices;
  createStore: (services: ConsoleServices, userId: string) => OnboardingStore;
}

export function App({ createServices, createStore }: AppProps) {
  const session = useSession();
  const services = useMemo(() => createServices(session.getToken), [createServices, session.getToken]);
  const store = useMemo(() => {
    if (session.status !== 'autenticado' || !session.userId) {
      return null;
    }

    return createStore(services, session.userId);
  }, [createStore, services, session.status, session.userId]);

  return (
    <ConsoleServicesProvider value={services}>
      <OnboardingProvider store={store} userId={session.userId}>
        <ConsoleShell />
      </OnboardingProvider>
    </ConsoleServicesProvider>
  );
}

function ConsoleShell() {
  const session = useSession();
  const { visiblePath, setVisiblePath } = useVisiblePath();
  const { draft, connection, loadStatus, flush, patchDraft } = useOnboarding();
  const [showErrors, setShowErrors] = useState<Record<string, boolean>>({});
  const normalizedVisiblePath = normalizePath(visiblePath);
  const visibleRoutePath = visiblePathForSession(normalizedVisiblePath, session.status);
  const route = useMemo(() => routeForPath(visibleRoutePath), [visibleRoutePath]);
  const progress = useMemo(() => ({ draft, connection }), [draft, connection]);

  const navigateTo = useCallback(
    (path: string) => {
      // Flush before leaving: without this, the last edit inside the debounce window is lost.
      void flush();
      pushPath(path);
      setVisiblePath(normalizePath(path));
    },
    [flush, setVisiblePath],
  );

  useEffect(() => {
    if (session.status !== 'autenticado') {
      return;
    }

    if (normalizedVisiblePath !== paths.welcome) {
      return;
    }

    replacePath(firstOperationalUrl());
    setVisiblePath(paths.store);
  }, [normalizedVisiblePath, session.status, setVisiblePath]);

  // Capture the landing handoff once the draft is loaded, and only when it is not already
  // recorded — a returning merchant must not have their original attribution overwritten by a
  // later visit.
  useEffect(() => {
    if (loadStatus !== 'pronto') {
      return;
    }

    const handoff = readLandingHandoff(window.location.search);

    if (!handoff) {
      return;
    }

    if (!draft.attribution.referenceCode) {
      patchDraft('attribution', handoff);
    }

    replacePath(pathWithoutHandoff(window.location.pathname, window.location.search));
  }, [loadStatus, draft.attribution.referenceCode, patchDraft]);

  const activeIndex = flowRoutes.findIndex((candidate) => candidate.path === route.path);
  const previousRoute = flowRoutes[activeIndex - 1];
  const nextRoute = flowRoutes[activeIndex + 1];

  function goNext() {
    if (!nextRoute) {
      return;
    }

    if (route.isComplete(progress)) {
      navigateTo(nextRoute.path);

      return;
    }

    setShowErrors((current) => ({ ...current, [route.path]: true }));
    focusFirstInvalid();
  }

  function goBack() {
    if (!previousRoute) {
      return;
    }

    navigateTo(previousRoute.path);
  }

  return (
    <main className="min-h-dvh bg-base-200">
      <header className="navbar min-h-16 flex-wrap gap-3 border-b border-base-300 bg-base-100 px-3 sm:px-8">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <BrandMark />
          <div className="min-w-0 leading-tight">
            <span className="block truncate font-semibold">Marketside</span>
            <span className="block truncate text-xs opacity-60">área da loja</span>
          </div>
        </div>
        <SessionStatus />
      </header>

      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-3 sm:gap-8 sm:p-8 lg:flex-row">
        <StepRail
          steps={flowRoutes}
          visiblePath={visibleRoutePath}
          progress={progress}
          onNavigate={navigateTo}
        />
        <div className="min-w-0 flex-1">
          <StepShell
            route={route}
            steps={flowRoutes}
            visiblePath={visibleRoutePath}
            showErrors={Boolean(showErrors[route.path])}
            onNavigate={navigateTo}
          />
          <NavigationArea
            route={route}
            previousRoute={previousRoute}
            nextRoute={nextRoute}
            onBack={goBack}
            onNext={goNext}
          />
        </div>
      </div>
    </main>
  );
}

function visiblePathForSession(visiblePath: string, sessionStatus: SessionAuthStatus): string {
  if (sessionStatus !== 'autenticado') {
    return visiblePath;
  }

  if (visiblePath !== paths.welcome) {
    return visiblePath;
  }

  return paths.store;
}

function firstOperationalUrl(): string {
  return pathWithCurrentSearch(paths.store);
}

function pathWithCurrentSearch(path: string): string {
  if (!window.location.search) {
    return path;
  }

  return `${path}${window.location.search}`;
}

function NavigationArea({
  route,
  previousRoute,
  nextRoute,
  onBack,
  onNext,
}: {
  route: ReturnType<typeof routeForPath>;
  previousRoute: ReturnType<typeof routeForPath> | undefined;
  nextRoute: ReturnType<typeof routeForPath> | undefined;
  onBack: () => void;
  onNext: () => void;
}) {
  if (route.usesOwnNavigation) {
    return null;
  }

  return (
    <NavigationButtons
      previousRoute={previousRoute}
      nextRoute={nextRoute}
      onBack={onBack}
      onNext={onNext}
      nextLabel={nextLabelFor(nextRoute)}
    />
  );
}

function nextLabelFor(nextRoute: ReturnType<typeof routeForPath> | undefined): string | undefined {
  if (nextRoute?.kind !== 'dashboard') {
    return undefined;
  }

  return 'Ir para o dashboard';
}

function focusFirstInvalid() {
  const invalid = document.querySelector<HTMLElement>('[aria-invalid="true"]');

  if (!invalid) {
    return;
  }

  invalid.focus();
}

function BrandMark() {
  return (
    <span className="text-primary" aria-hidden="true">
      <svg viewBox="0 0 32 32" className="size-8">
        <path
          d="M16 3C9.4 3 4 8.4 4 15c0 2.5 1 4.8 2.7 6.6L5 27.5l6.1-1.6c1.5.8 3.2 1.3 4.9 1.3 6.6 0 12-5.4 12-12S22.6 3 16 3z"
          fill="currentColor"
        />
        <path
          d="M10 12.9v2.2M13 10.8v6.4M16 8.7v10.6M19 10.8v6.4M22 12.9v2.2"
          fill="none"
          stroke="var(--color-base-100)"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}
